import type { UserRole } from '../types';

export type QuestionSystemType = 'simulation' | 'workbook' | 'past_paper' | 'topic_practice' | 'random_paper' | 'mooc_topic_practice' | 'mooc_random';

export type UserExamRestrictions = {
	success: boolean;
	hsk2Levels: number[];
	hsk3Levels: number[];
	cscaLevels: number[];
	hsk2Restricted: boolean;
	hsk3Restricted: boolean;
	cscaRestricted: boolean;
};

export type LoginResponse = { token: string; user: { id: string; email: string; role: UserRole; createdAt: string } };
export type QuestionChangeRequest = {
  id: string;
  question_id: string;
  bank_version: '2.0' | '3.0';
  change_type: 'create' | 'update' | 'delete';
  source_role: UserRole;
  submitted_by: string;
  submitted_at: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_comment?: string | null;
  original_snapshot?: any;
  proposed_snapshot?: any;
};
export type StartTestResponse = { 
  test_session_id: string; 
  questions: {
    passage_id?: string;
    passage_text?: string;
    question_prompt?: string;
    question_id: string; 
    question_text: string; 
    options: string[]; 
    media: string|null;
    part?: string;
    explanation?: string;
    order?: number[];
	question_number?: number | null;
	level?: number;
	grading_pending?: boolean;
  }[];
  resumed?: boolean;
  progress?: {
    index: number;
    answers: Record<string, number | string | string[] | null>;
    revision?: number;
  };
	bank_version?: string;
	system_type?: QuestionSystemType;
	workbook_part?: string | null;
	paper_id?: string | null;
	paper_year?: number | null;
	paper_month?: number | null;
	topic_coarse?: string | null;
	topic_fine?: string | null;
	exam_title?: string | null;
	started_at?: string;
	duration_seconds?: number;
	server_now?: string;
	random_blueprint?: CscaRandomTopicAllocation[] | null;
};

export type CscaSubjectCatalogItem = {
	level: number;
	code: string;
	name: string;
	enabled: boolean;
	available: boolean;
};

export type CscaPastPaperCatalogItem = {
	paperKind?: 'review';
	pending_count?: number;
	sourceNote?: string;
	missingNumbers?: number[];
	id: string;
	level: number;
	subject: string;
	subjectCode: string;
	year: number;
	month: number;
	title: string;
	durationSeconds: number;
	enabled: boolean;
	question_count: number;
	available: boolean;
	active_test_id: string | null;
	history_count: number;
	latest_score: number | null;
	latest_total: number | null;
	latest_percentage: number | null;
};

export type TestCatalogResponse = {
	bank_version: string;
	system_type: QuestionSystemType;
	levels: Array<{
		level: number;
		question_count: number;
		parts: Array<{ level: number; part: string; question_count: number }>;
	}>;
	subjects?: CscaSubjectCatalogItem[];
	papers?: CscaPastPaperCatalogItem[];
};

export type CscaTopicMetrics = {
	question_count: number;
	question_probability: number;
	question_probability_percent: number;
	within_coarse_probability_percent: number | null;
	paper_coverage_count: number;
	paper_coverage_percent: number;
	average_per_paper: number;
	estimated_count_in_48_questions: number;
	min_per_paper: number;
	max_per_paper: number;
	per_paper: Array<{ paper_id: string; title: string; question_count: number }>;
	question_positions: Array<{
		position: number;
		position_label: string;
		question_count: number;
		paper_coverage_count: number;
		appearance_probability_percent: number;
	}>;
};

export type CscaTopicAnalysisResponse = {
	bank_version: 'CSCA';
	system_type: 'past_paper' | 'mooc_topic_practice';
	source_mode?: 'past' | 'mooc';
	level: number;
	subject: string;
	paper_count: number;
	total_questions: number;
	classified_questions: number;
	unclassified_questions: number;
	average_questions_per_paper: number;
	complete_set_count?: number;
	complete_set_question_count?: number;
	simulation_question_count?: number;
	ai_question_count?: number;
	methodology: string;
	disclaimer: string;
	topics: Array<CscaTopicMetrics & {
		coarse_type: string;
		fine_types: Array<CscaTopicMetrics & { fine_type: string }>;
	}>;
};

export type CscaRandomTopicAllocation = {
	coarse_type: string;
	historical_question_count: number;
	historical_average_per_paper: number;
	question_count: number;
	fine_types: Array<{
		fine_type: string;
		historical_question_count: number;
		historical_average_per_paper: number;
		question_count: number;
	}>;
};

export type CscaRandomBlueprintResponse = {
	bank_version: 'CSCA';
	system_type: 'random_paper';
	level: number;
	subject: string;
	source_paper_count: number;
	source_question_count: number;
	total_questions: 48;
	duration_seconds: number;
	allocation_method: string;
	position_rule: string;
	positions: Array<{
		position: number;
		position_label: string;
		candidate_count: number;
	}>;
	topics: CscaRandomTopicAllocation[];
};
export type SubmitTestResponse = {
  score: number;
  total: number;
  wrong_items: { question_id: string; question_text: string; options: string[]; correct_option_index: number; explanation: string | null }[];
  vocab_summary_id: string;
  weighted_score: number;
  max_score: number;
  section_scores: {
    listening: number;
    reading: number;
    writing: number;
  };
};

export type WritingGrading = {
  score: number | null;
  summary: string;
  dimensions: { name: string; score: number | null; comment: string }[];
  corrections: { original: string; suggestion: string; reason: string }[];
  sample: string;
};

export type { W2SentenceAnalysisResponse, W2SentenceGuide } from '../../../shared/w2SentenceAnalysis';
import type { W2SentenceAnalysisResponse } from '../../../shared/w2SentenceAnalysis';

export type W1GrammarStructure = {
  pattern: string;
  example: string;
  note: string;
};

export type W1GrammarCategory = {
  id: string;
  bank_version: string;
  level: number;
  name: string;
  description: string;
  structures: W1GrammarStructure[];
  sort_order: number;
  is_active: boolean;
  is_fallback: boolean;
  created_at: string;
  updated_at: string;
};

export type W1GrammarQuestion = {
  question_id: string;
  shuffled: string;
  shuffled_parts: string[];
  correct_answer: string;
};

export type W1GrammarAnalysisCategory = W1GrammarCategory & {
  questions: W1GrammarQuestion[];
};

export type W1GrammarAnalysisResponse = {
  success: boolean;
  model: string;
  bank_version: string;
  level: number;
  question_count: number;
  fallback_count: number;
  categories: W1GrammarAnalysisCategory[];
};

export type W1GrammarCategoryInput = {
  bankVersion: string;
  level: number;
  name: string;
  description: string;
  structures: W1GrammarStructure[];
  sortOrder: number;
  isActive: boolean;
};

export const API_BASE_URL = BACKEND_URL;
const API_BASE = API_BASE_URL;
console.log('API_BASE_DEBUG:', API_BASE);

try {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  if (host.includes('pages.dev')) {
    // Redundant but safe
    // API_BASE = 'https://huanlemandarinhsk.huanle-mandarin.workers.dev/api';
  }
} catch {}

async function request<T>(path: string, options: RequestInit = {}, token?: string): Promise<T> {
const headers: Record<string,string> = { 'Content-Type': 'application/json', ...(options.headers as any) };
if (token) headers['Authorization'] = `Bearer ${token}`;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const fullUrl = `${API_BASE}${normalizedPath}`;
  const method = (options.method || 'GET').toUpperCase();
  console.log('API Request:', fullUrl, options.method || 'GET');
  const res = await fetch(fullUrl, { ...options, headers, cache: method === 'GET' ? 'no-store' : options.cache });
if (!res.ok) {
let msg = 'Request failed';
let stage = '';
try {
  const j = await res.json();
  msg = j?.message || msg;
  stage = typeof j?.stage === 'string' ? j.stage : '';
} catch {}
const error = new Error(stage ? `${msg} (${stage})` : msg) as Error & { status?: number; stage?: string };
error.status = res.status;
error.stage = stage || undefined;
throw error;
}
// A reviewed/recalculated test must not keep an earlier roster summary cached.
if (method !== 'GET' && /^\/admin\/(?:test\/|question\/)/.test(normalizedPath)) learningOverviewCache.clear();
if (res.status === 204) return undefined as any;
return res.json();
}

let learningOverviewToken = '';
const learningOverviewCache = new Map<string, { at: number; promise: Promise<{ success: boolean; restrictions: UserExamRestrictions; summaries: LearningSummary[] }> }>();

export const api = {
	login(identifier: string, password: string) {
		const isEmail = identifier.includes('@');
		const payload = isEmail ? { email: identifier, password } : { username: identifier, password };
		return request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(payload) });
	},
	startTest(level: number, part: string, num_questions: number, token: string, partCounts?: Array<{ part: string; count: number }>, options?: { bankVersion?: string; systemType?: QuestionSystemType; workbookPart?: string; paperId?: string; coarseType?: string; fineType?: string; test_session_id?: string; start_new?: boolean }) {
return request<StartTestResponse>('/test/start', { method: 'POST', body: JSON.stringify({ level, part, num_questions, partCounts, ...options }) }, token);
},
	getTestCatalog(token: string, options: { bankVersion: string; systemType: QuestionSystemType; level?: number }) {
		const params = new URLSearchParams({ bankVersion: options.bankVersion, systemType: options.systemType });
		if (options.level) params.set('level', String(options.level));
		return request<TestCatalogResponse>(`/test/catalog?${params.toString()}`, {}, token);
	},
	getCscaCatalog(token: string, level?: number) {
		return api.getTestCatalog(token, { bankVersion: 'CSCA', systemType: 'past_paper', level });
	},
	getCscaTopicAnalysis(token: string, level = 3) {
		return request<CscaTopicAnalysisResponse>(`/test/csca/topics?level=${encodeURIComponent(String(level))}`, {}, token);
	},
	getCscaMoocTopics(token: string, level = 3) {
		return request<CscaTopicAnalysisResponse>(`/test/csca/mooc/topics?level=${encodeURIComponent(String(level))}`, {}, token);
	},
	getCscaRandomBlueprint(token: string, level = 3) {
		return request<CscaRandomBlueprintResponse>(`/test/csca/random-blueprint?level=${encodeURIComponent(String(level))}`, {}, token);
	},
	startCscaPaper(paperId: string, token: string) {
		return request<StartTestResponse>('/test/start', {
			method: 'POST',
			body: JSON.stringify({ bankVersion: 'CSCA', systemType: 'past_paper', paperId }),
		}, token);
	},
	startCscaTopicPractice(coarseType: string, fineType: string | null, count: number, token: string, level = 3) {
		return request<StartTestResponse>('/test/start', {
			method: 'POST',
			body: JSON.stringify({
				bankVersion: 'CSCA',
				systemType: 'topic_practice',
				level,
				part: level === 2 ? 'chinese-stem' : level === 1 ? 'chinese-humanities' : 'math',
				num_questions: count,
				coarseType,
				fineType: fineType || undefined,
			}),
		}, token);
	},
	startCscaMoocTopicPractice(coarseType: string, fineType: string | null, count: number, token: string) {
		return request<StartTestResponse>('/test/start', {
			method: 'POST',
			body: JSON.stringify({
				bankVersion: 'CSCA',
				systemType: 'mooc_topic_practice',
				level: 3,
				part: 'math',
				num_questions: count,
				coarseType,
				fineType: fineType || undefined,
			}),
		}, token);
	},
	startCscaRandomPaper(token: string) {
		return request<StartTestResponse>('/test/start', {
			method: 'POST',
			body: JSON.stringify({ bankVersion: 'CSCA', systemType: 'random_paper', level: 3, part: 'math', num_questions: 48 }),
		}, token);
	},
	startCscaMoocRandom(token: string) {
		return request<StartTestResponse>('/test/start', {
			method: 'POST',
			body: JSON.stringify({ bankVersion: 'CSCA', systemType: 'mooc_random', level: 3, part: 'math', num_questions: 48 }),
		}, token);
	},
	gradeWriting(payload: { prompt: string; answer: string }, token: string) {
		return request<{ success: boolean; grading: WritingGrading }>('/ai/grade-writing', { method: 'POST', body: JSON.stringify(payload) }, token);
	},
submitTest(test_session_id: string, answers: { question_id: string; chosen_option_index: number | null; chose_buzhidao: boolean; time_spent_seconds?: number; chosen_sequence?: string[] | null; chosen_text_answer?: string | null }[], token: string, options?: { elapsedSeconds?: number }) {
return request<SubmitTestResponse>('/test/submit', { method: 'POST', body: JSON.stringify({ test_session_id, answers, elapsed_seconds: options?.elapsedSeconds }) }, token);
},
getHistory(token: string) {
return request<any[]>('/user/history', {}, token);
},
getTestDetail(id: string, token: string) {
    return request<any>(`/user/test/${id}`, {}, token);
  },
  getAdminTestDetail(id: string, token: string) {
    return request<any>(`/admin/test/${id}`, {}, token);
  },
  updateAdminQuestionCorrectAnswer(questionId: string, correctIndex: number, token: string) {
    return request<{ success: boolean; question_id: string; previous_correct_index: number; correct_index: number }>(
      `/admin/questions/${questionId}/correct-answer`,
      { method: 'POST', body: JSON.stringify({ correct_index: correctIndex }) },
      token
    );
  },
  updateAdminQuestionContent(questionId: string, questionText: string, options: string[], correctIndex: number, token: string) {
    return request<{ success: boolean; question_id: string; question_text: string; options: string[]; correct_index: number }>(
      `/admin/questions/${questionId}/content`,
      { method: 'POST', body: JSON.stringify({ question_text: questionText, options, correct_index: correctIndex }) },
      token
    );
  },
  createQuestionChangeRequest(payload: { question_id?: string; bankVersion?: string; change_type: 'create' | 'update' | 'delete'; proposed_snapshot?: any }, token: string) {
    return request<{ success: boolean; request: QuestionChangeRequest }>(
      '/question-change-requests',
      { method: 'POST', body: JSON.stringify(payload) },
      token
    );
  },
  recalculateAdminTestScore(testId: string, token: string) {
    return request<{ success: boolean; score: number; total: number; weighted_score: number; max_score: number; updated_items: number }>(
      `/admin/test/${testId}/recalculate-score`,
      { method: 'POST' },
      token
    );
  },
  getWrongHistory(token: string) {
    return request<{ success: boolean; history: Array<{ id: string; date: string; level: number; part: string; items: any[] }> }>('/user/wrong-history', {}, token);
  },
  getAdminUsers(token: string, options: {
    page?: number; limit?: number; search?: string;
    family?: 'all' | '2.0' | '3.0' | 'CSCA'; tests?: 'all' | 'has' | 'none';
    teacher?: string; access?: 'all' | 'approved' | 'pending';
    sort?: 'tests' | 'name' | 'joined'; direction?: 'asc' | 'desc';
  } = {}) {
    const params = new URLSearchParams({
      page: String(options.page || 1),
      limit: String(options.limit || 10),
    });
    if (options.search) params.set('search', options.search);
    if (options.family && options.family !== 'all') params.set('family', options.family);
    if (options.tests && options.tests !== 'all') params.set('tests', options.tests);
    if (options.teacher && options.teacher !== 'all') params.set('teacher', options.teacher);
    if (options.access && options.access !== 'all') params.set('access', options.access);
    if (options.sort && options.sort !== 'tests') params.set('sort', options.sort);
    if (options.direction && options.direction !== 'desc') params.set('direction', options.direction);
    return request<{ success: boolean; users: Array<{ id: string; username: string; email: string; created_at: string; test_count: number; avg_score: number | null; composite_score?: number | null; teacher_access_approved?: boolean; teacher_access_approved_at?: string | null; teacher_access_approved_by?: string | null; assigned_teacher_id?: string | null; assigned_teacher_username?: string | null; }>; pagination: { page: number; limit: number; total: number; total_pages: number } }>(`/admin/users?${params.toString()}`, {}, token);
  },
  getAdminTeachers(token: string) {
    return request<{ success: boolean; teachers: Array<{ id: string; username: string; email: string }> }>('/admin/teachers', {}, token);
  },
  setAdminUserTeacherAccess(userId: string, approved: boolean, token: string) {
    return request<{ success: boolean; approved: boolean }>(`/admin/users/${userId}/teacher-access`, {
      method: 'POST',
      body: JSON.stringify({ approved })
    }, token);
  },
  setAdminUserTeacherAssignment(userId: string, teacherUserId: string | null, token: string) {
    return request<{ success: boolean; assigned_teacher_id: string | null }>(`/admin/users/${userId}/teacher-assignment`, {
      method: 'POST',
      body: JSON.stringify({ teacherUserId })
    }, token);
  },
  adminUpload(content: any[], token: string) {
    return request<any>('/admin/upload-json', { method: 'POST', body: JSON.stringify({ content }) }, token);
  },
adminStats(token: string) {
    return request<any>('/admin/stats', {}, token);
  },
  getAdminUserHistory(userId: string, token: string, options: { page?: number; limit?: number; bankVersion?: string; level?: number | null; category?: string } = {}) {
    const params = new URLSearchParams({
      page: String(options.page || 1),
      limit: String(options.limit || 10),
      _ts: String(Date.now()),
    });
    if (options.category) params.set('category', options.category);
    if (options.bankVersion && options.bankVersion !== 'all') params.set('bankVersion', options.bankVersion);
    if (options.level) params.set('level', String(options.level));
    return request<{ success: boolean; history: Array<{ id: string; date_taken: string; completed_at?: string | null; level: number; part: string; bank_version: string; system_type?: QuestionSystemType; workbook_part?: string | null; paper_id?: string | null; paper_year?: number | null; paper_month?: number | null; topic_coarse?: string | null; topic_fine?: string | null; weighted_score: number; max_score: number; review_status?: 'pending' | 'reviewed'; reviewed_by?: string | null; reviewed_at?: string | null }>; pagination: { page: number; limit: number; total: number; total_pages: number } }>(`/admin/users/${userId}/history?${params.toString()}`, {}, token);
  },
  getAdminUserReviewStatuses(userId: string, testIds: string[], token: string) {
    const params = new URLSearchParams({
      ids: testIds.join(','),
      _ts: String(Date.now()),
    });
    return request<{ success: boolean; statuses: Array<{ id: string; review_status?: 'pending' | 'reviewed'; reviewed_by?: string | null; reviewed_at?: string | null }> }>(`/admin/users/${userId}/review-statuses?${params.toString()}`, {}, token);
  },
  markAdminTestReviewed(testId: string, token: string) {
    return request<{ success: boolean; test: { id: string; review_status: 'pending' | 'reviewed'; reviewed_by?: string | null; reviewed_at?: string | null } }>(
      `/admin/test/${testId}/mark-reviewed`,
      { method: 'POST' },
      token
    );
  },
  getAdminUserWrongHistory(userId: string, token: string, filters: { level?: number | null; bankVersion?: string | null; category?: string | null } = {}) {
    const params = new URLSearchParams();
    if (filters.level) params.set('level', String(filters.level));
    if (filters.bankVersion) params.set('bankVersion', filters.bankVersion);
    if (filters.category) params.set('category', filters.category);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return request<{ success: boolean; history: Array<{ id: string; date: string; level: number; part: string; bank_version: string; items: any[] }> }>(`/admin/users/${userId}/wrong-history${suffix}`, {}, token);
  },
  getAdminW1GrammarCategories(token: string, bankVersion: string, level: number) {
    const params = new URLSearchParams({ bankVersion, level: String(level) });
    return request<{ success: boolean; categories: W1GrammarCategory[] }>(`/admin/w1-grammar-categories?${params.toString()}`, {}, token);
  },
  createAdminW1GrammarCategory(token: string, input: W1GrammarCategoryInput) {
    return request<{ success: boolean; category: W1GrammarCategory }>('/admin/w1-grammar-categories', {
      method: 'POST',
      body: JSON.stringify(input)
    }, token);
  },
  updateAdminW1GrammarCategory(token: string, categoryId: string, input: W1GrammarCategoryInput) {
    return request<{ success: boolean; category: W1GrammarCategory }>(`/admin/w1-grammar-categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(input)
    }, token);
  },
  deleteAdminW1GrammarCategory(token: string, categoryId: string) {
    return request<{ success: boolean }>(`/admin/w1-grammar-categories/${categoryId}`, { method: 'DELETE' }, token);
  },
  analyzeAdminUserW1Grammar(userId: string, token: string, bankVersion: string, level: number) {
    return request<W1GrammarAnalysisResponse>(`/admin/users/${userId}/w1-grammar-analysis`, {
      method: 'POST',
      body: JSON.stringify({ bankVersion, level })
    }, token);
  },
  analyzeAdminUserW2Sentences(userId: string, token: string, category: string, questionIds: string[]) {
    return request<W2SentenceAnalysisResponse>(`/admin/users/${userId}/w2-sentence-analysis`, {
      method: 'POST', body: JSON.stringify({ bankVersion: '2.0', level: 4, category, questionIds })
    }, token);
  },
  deleteAdminUserTest(userId: string, testId: string, token: string) {
    return request<{ success: boolean; message: string }>(`/admin/users/${userId}/tests/${testId}`, {
      method: 'DELETE'
    }, token);
  },
  getAdminUserStats(userId: string, token: string) {
    return request<{ success: boolean; stats: Array<{ level: number; bank_version: string; total_tests: number; avg_score: number }>; category_stats: Array<{ level: number; category: string; total_score: number; total_max_score: number; test_count: number }> }>(`/admin/users/${userId}/stats`, {}, token);
  },
  getUserStats(token: string) {
    return request<{ success: boolean; stats: Array<{ level: number; total_tests: number; avg_score: number }>; category_stats: Array<{ level: number; category: string; total_score: number; total_max_score: number; test_count: number }> }>(`/user/stats`, {}, token);
  },
  getAdminUserDetailedStats(userId: string, token: string) {
    return request<{ success: boolean; stats: Array<{ level: number; listening: number; reading: number; writing: number; total: number }> }>(`/admin/users/${userId}/detailed-stats`, {}, token);
  },
  recalcAdminStats(userId: string, token: string) {
    learningOverviewCache.delete(userId);
    return request<{ success: boolean; processed: number }>(`/admin/users/${userId}/recalc-stats`, { method: 'POST' }, token);
  },
  resetAdminUserTests(userId: string, password: string, includeVocab: boolean, token: string) {
    learningOverviewCache.delete(userId);
    return request<{ success: boolean; message: string }>('/admin/users/' + userId + '/reset-tests', {
        method: 'POST',
        body: JSON.stringify({ password, includeVocab })
    }, token);
  },
  getAdminUserVocab(userId: string, token: string, page: number = 1, limit: number = 20, versions: string[] = [], levels: string[] = []) {
    const offset = (page - 1) * limit;
    let url = `/admin/users/${userId}/vocab?limit=${limit}&offset=${offset}`;
    if (versions.length > 0) url += `&versions=${versions.join(',')}`;
    if (levels.length > 0) url += `&levels=${levels.join(',')}`;
    return request<{ success: boolean; vocab: Array<any>; total: number; page: number; totalPages: number; hasProficiencyData?: boolean }>(url, {}, token);
  },
  getAdminUserVocabStats(userId: string, token: string, version: string) {
    return request<{ success: boolean; stats: { red: number; yellow: number; green: number; total: number } }>(`/admin/users/${userId}/vocab-stats?version=${encodeURIComponent(version)}`, {}, token);
  },
  setAdminUserVocabProficiency(userId: string, version: string, levels: string[], proficiency: number, token: string) {
    return request<{ success: boolean; updated: number; message?: string }>('/admin/users/' + userId + '/vocab-proficiency', {
        method: 'POST',
        body: JSON.stringify({ version, levels, proficiency })
    }, token);
  },
  deleteAdminUserVocabProficiency(userId: string, token: string) {
    return request<{ success: boolean; message: string }>('/admin/users/' + userId + '/vocab-proficiency', {
        method: 'DELETE'
    }, token);
  },
  getWeakVocab(testId: string, token: string) {
    return request<{ success: boolean; vocab: Array<any> }>(`/user/test/${testId}/weak-vocab`, {}, token);
  },
  updateVocabReview(items: { vocab_id: string; outcome: 'correct'|'wrong'|'dont_know' }[], token: string, version: '2.0' | '3.0' | 'CSCA') {
    return request<{ success: boolean; updated: number }>('/vocab/review-update', { method: 'POST', body: JSON.stringify({ items, version }) }, token);
  },
  generateRandomTest(config: any, token: string) {
    return request<any>('/test/random', { method: 'POST', body: JSON.stringify(config) }, token);
  },
  getUserVocabStats(token: string, version: '2.0' | '3.0' | 'CSCA', recommendedOnly = false) {
    return request<{
      success: boolean;
      distribution: { weak: number; mastered: number; total: number };
      recommended: Array<{ vocab_id: string; hanzi: string; pinyin: string; En_meaning: string; level: number; proficiency: number; appear: number; correct: number; updated_at: string | null }>;
    }>(`/user/vocab-stats?version=${encodeURIComponent(version)}${recommendedOnly ? '&recommended_only=1' : ''}`, {}, token);
  },
  getUserVocab(token: string, version: '2.0' | '3.0' | 'CSCA', page: number = 1, limit: number = 20, levels: string[] = [], sortBy: string = 'proficiency', sortDir: string = 'asc') {
    const offset = (page - 1) * limit;
    let url = `/user/vocab?version=${encodeURIComponent(version)}&limit=${limit}&offset=${offset}&sort_by=${sortBy}&sort_dir=${sortDir}`;
    if (levels.length > 0) url += `&levels=${levels.join(',')}`;
    return request<{ success: boolean; vocab: Array<any>; total: number; page: number; totalPages: number; hasProficiencyData?: boolean }>(url, {}, token);
  },
  getUserRestrictions(token: string) {
    return request<UserExamRestrictions>('/user/restrictions', {}, token);
  },
  getUserExamTrends(token: string) {
    return request<{ success: boolean; restrictions: UserExamRestrictions; events: Array<{
      id: string; version: '2.0' | '3.0' | 'CSCA'; level: number;
      part: 'simulation' | 'listening' | 'reading' | 'writing'; completedAt: string;
      scores: { total: number | null; listening: number | null; reading: number | null; writing: number | null };
    }> }>('/user/exam-trends', {}, token);
  },
  getStudentRestrictions(userId: string, token: string) {
    return request<UserExamRestrictions>(`/admin/users/${userId}/restrictions`, {}, token);
  },
  getStudentLearningOverview(userId: string, token: string, refresh = false) {
    if (learningOverviewToken !== token) { learningOverviewCache.clear(); learningOverviewToken = token; }
    const cached = learningOverviewCache.get(userId);
    if (!refresh && cached && Date.now() - cached.at < 60000) return cached.promise;
    const promise = request<{ success: boolean; restrictions: UserExamRestrictions; summaries: LearningSummary[] }>(`/admin/users/${userId}/learning-overview`, {}, token);
    if (learningOverviewCache.size >= 50) learningOverviewCache.delete(learningOverviewCache.keys().next().value!);
    learningOverviewCache.set(userId, { at: Date.now(), promise });
    promise.catch(() => { if (learningOverviewCache.get(userId)?.promise === promise) learningOverviewCache.delete(userId); });
    return promise;
  },
};

export type LearningSummary = {
  family: string; category: 'past' | 'simulation' | 'practice'; level: number;
  total_tests: number; pending_review: number; pending_questions: number;
  latest_id: string; latest_at: string; score: number | null; max_score: number;
  sections: Record<string, { score: number; max: number }>; workbook: boolean; status?: string;
};
